import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Globe, Search, Palette, TrendingUp, Database, Target, ArrowRight,
} from 'lucide-react';
import { useCMS } from '../context/CMSContext';
import { services as defaultServices } from '../data/mockData';

const ICON_MAP = { Globe, Search, Palette, TrendingUp, Database, Target };

const FADE_UP = { opacity: 0, y: 20 };
const VISIBLE = { opacity: 1, y: 0 };

const ServiceCard = ({ service, index, inView }) => {
  const Icon = ICON_MAP[service.icon] || Globe;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? VISIBLE : {}}
      transition={{ delay: 0.05 * index, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex flex-col justify-between min-h-[280px] p-8 rounded-2xl bg-[#0B1221] border border-white/5 hover:border-white/15 hover-border-glow transition-all duration-500 hover:-translate-y-1"
      data-testid={`service-card-${service.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-white group-hover:border-white transition-all duration-500">
          <Icon className="w-5 h-5 text-white group-hover:text-[#050914] transition-colors duration-500" />
        </div>
        <span className="text-xs tracking-[0.25em] text-white/30 font-bold">{service.number}</span>
      </div>
      <div className="mt-8">
        <h3 className="font-display text-xl lg:text-2xl font-medium tracking-tight text-white leading-tight">
          {service.title}
        </h3>
        <p className="mt-3 text-white/50 text-sm leading-relaxed font-light line-clamp-3">
          {service.description}
        </p>
      </div>
    </motion.div>
  );
};

const ServicesPreview = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const { get } = useCMS();
  const services = get('services') || defaultServices;

  return (
    <section id="services" ref={ref} className="relative py-24 lg:py-32 bg-[#050914] border-t border-white/5" data-testid="services-preview">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <motion.p initial={FADE_UP} animate={inView ? VISIBLE : {}}
              className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
              <span className="w-8 h-px bg-white/30" /> What We Do
            </motion.p>
            <motion.h2 initial={FADE_UP} animate={inView ? VISIBLE : {}} transition={{ delay: 0.1 }}
              className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tighter text-white leading-[1.05]">
              End-to-end services for growth-focused industrial brands.
            </motion.h2>
          </div>
          <Link to="/services"
            className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white text-sm font-semibold hover:bg-white/5 transition-colors"
            data-testid="services-view-all">
            Explore all services <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.slice(0, 6).map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;
